/**
 * Deltid (issue #109): each part-timer's approved hours in the last twelve finished months against
 * their agreed week (GET /admin/part-time, pay.view), with the right in arbeidsmiljøloven § 14-4 a
 * flagged when the twelve-month average passes the agreed position. Read only: it informs, it decides
 * nothing. Whether the need for the extra work is over is the employer's to document; Timefly cannot
 * know it, and the page says so.
 */
import { errorText, get } from '@/api/client';
import { T } from '@/i18n';
import { Button, Skeleton, toast } from '@/ui';
import { fmtD, monthLabel, num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { Explain, Notice, Status, TabPanel } from '@/ui/page';
import { useQuery } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { csvOf } from '../dagen/today';
import { LonnHead } from './Head';

interface Month {
  month: string;
  days: number;
  agreed: number;
  worked: number;
  diff: number;
  percent: number;
}
export interface PartTimer {
  user_id: number;
  name: string;
  lastname: string;
  agreed_week_hours: number;
  agreed_percent: number;
  since: string | null;
  since_first_shift: boolean;
  months_counted: number;
  full_year: boolean;
  agreed: number;
  worked: number;
  diff: number;
  worked_percent: number;
  months_over: number;
  flag: boolean;
  months: Month[];
}
interface Report {
  from: string;
  to: string;
  full_week: number;
  people: PartTimer[];
}

const FLAG =
  'Har jevnlig arbeidet utover avtalt stilling i 12 måneder; rett til stilling tilsvarende faktisk arbeidstid (aml § 14-4 a)';
const dec = (n: number) => n.toFixed(2).replace('.', ',');

/** Why fewer than twelve months count, or nothing. */
function shortOf(p: PartTimer): string | null {
  if (p.full_year) return null;
  if (p.since_first_shift)
    return T('Ingen startdato: regnet fra første godkjente vakt, {n} av 12 måneder.', {
      n: p.months_counted,
    });
  return T('Ansatt fra {date}: {n} av 12 måneder. Loven regner 12.', {
    date: p.since ? fmtD(p.since) : '–',
    n: p.months_counted,
  });
}

export default function Deltid() {
  const q = useQuery({
    queryKey: ['part-time'],
    queryFn: () => get<Report>('/admin/part-time'),
    staleTime: 30_000,
  });
  const [open, setOpen] = useState<number | null>(null);
  const r = q.data;
  const people = r?.people ?? [];

  const exportCsv = () => {
    if (!r) return;
    const rows = [
      [
        'Ansatt',
        'Måned',
        'Dager',
        'Avtalt (t)',
        'Jobbet (t)',
        'Differanse (t)',
        'Stilling jobbet (%)',
        'Avtalt stilling (%)',
        'Flagget',
      ],
    ];
    for (const p of people) {
      for (const m of p.months) {
        rows.push([
          `${p.name} ${p.lastname}`,
          m.month,
          String(m.days),
          dec(m.agreed),
          dec(m.worked),
          dec(m.diff),
          String(m.percent).replace('.', ','),
          String(p.agreed_percent).replace('.', ','),
          p.flag ? 'Ja' : 'Nei',
        ]);
      }
    }
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csvOf(rows)], { type: 'text/csv;charset=utf-8' }));
    a.download = `deltid-${r.from}-${r.to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast(T('Deltid lastet ned'));
  };

  return (
    <div className="lonn">
      <LonnHead
        tab="deltid"
        action={
          <Button tone="pri" onClick={exportCsv} disabled={!people.length} data-testid="deltid-csv">
            <Icon name="download" />
            {T('Last ned CSV')}
          </Button>
        }
      />
      <TabPanel tab="deltid">
        <section className="mcard" data-testid="deltid">
          <p style={{ margin: 0 }}>
            {r
              ? T(
                  'Godkjente timer {from} til {to} mot avtalt arbeidstid. Full stilling er {week} timer i uka.',
                  {
                    from: fmtD(r.from),
                    to: fmtD(r.to),
                    week: num(r.full_week),
                  },
                )
              : T('Godkjente timer de siste 12 hele månedene mot avtalt arbeidstid.')}
          </p>
          <Explain q={T('Hva sier loven, og hva vet ikke Timefly?')}>
            <p>
              {T(
                'Arbeidsmiljøloven § 14-4 a: deltidsansatte som de siste 12 månedene jevnlig har arbeidet utover avtalt arbeidstid, har rett til stilling tilsvarende faktisk arbeidstid i perioden.',
              )}
            </p>
            <p>
              {T(
                'Retten gjelder ikke hvis arbeidsgiveren kan dokumentere at behovet for merarbeidet ikke lenger finnes. Det kan ikke Timefly vite. Rapporten viser timene; den avgjør ikke saken, og den endrer ingenting.',
              )}
            </p>
            <p>
              {T(
                'Loven regner de 12 månedene fra den dagen den ansatte krever stillingen. Rapporten viser de 12 siste hele månedene. Timene er de samme godkjente timene som lønnsgrunnlaget, med pause trukket. Fravær og ferie er ikke regnet inn, og avtalt arbeidstid er den som står på den ansatte i dag.',
              )}
            </p>
          </Explain>
          {q.isPending ? (
            <Skeleton />
          ) : q.isError ? (
            <Notice tone="dgr" title={T('Kunne ikke hente rapporten')}>
              {errorText(q.error)}
            </Notice>
          ) : people.length === 0 ? (
            <p style={{ color: 'var(--ink3)' }}>
              {T(
                'Ingen ansatte har avtalt arbeidstid under full stilling. Avtalt arbeidstid settes på den ansatte.',
              )}
            </p>
          ) : (
            <table className="t">
              <thead>
                <tr>
                  <th>{T('Ansatt')}</th>
                  <th>{T('Avtalt stilling')}</th>
                  <th>{T('Jobbet')}</th>
                  <th>{T('Differanse')}</th>
                  <th>{T('Måneder over')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {people.map((p) => (
                  <Fragment key={p.user_id}>
                    <tr data-testid={`deltid-${p.user_id}`}>
                      <td>
                        {p.name} {p.lastname}
                        {p.flag && (
                          <div>
                            <Status tone="warn">{T(FLAG)}</Status>
                            <div style={{ fontSize: 13, color: 'var(--ink3)' }}>
                              {T(
                                'Timene tilsvarer {p} % stilling. Gjelder ikke hvis behovet for merarbeidet ikke lenger finnes; det vet ikke Timefly.',
                                {
                                  p: num(p.worked_percent, 1),
                                },
                              )}
                            </div>
                          </div>
                        )}
                        {shortOf(p) && <div style={{ fontSize: 13, color: 'var(--ink3)' }}>{shortOf(p)}</div>}
                      </td>
                      <td>
                        {num(p.agreed_percent, 1)} % ({T('{h} t/uke', { h: num(p.agreed_week_hours) })})
                      </td>
                      <td>
                        {num(p.worked_percent, 1)} % ({num(p.worked)} t)
                      </td>
                      <td>{`${p.diff > 0 ? '+' : ''}${num(p.diff)} t`}</td>
                      <td>{T('{n} av {m}', { n: p.months_over, m: p.months_counted })}</td>
                      <td>
                        <Button
                          ghost
                          onClick={() => setOpen(open === p.user_id ? null : p.user_id)}
                          aria-expanded={open === p.user_id}
                        >
                          {T('Måneder')}
                        </Button>
                      </td>
                    </tr>
                    {open === p.user_id && (
                      <tr>
                        <td colSpan={6}>
                          <table className="t" data-testid={`deltid-mnd-${p.user_id}`}>
                            <thead>
                              <tr>
                                <th>{T('Måned')}</th>
                                <th>{T('Avtalt')}</th>
                                <th>{T('Jobbet')}</th>
                                <th>{T('Differanse')}</th>
                                <th>{T('Stilling')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {p.months.map((m) => (
                                <tr key={m.month}>
                                  <td>{monthLabel(m.month)}</td>
                                  <td>{num(m.agreed)} t</td>
                                  <td>{num(m.worked)} t</td>
                                  <td>{`${m.diff > 0 ? '+' : ''}${num(m.diff)} t`}</td>
                                  <td>{num(m.percent, 1)} %</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </TabPanel>
    </div>
  );
}
